import { logger } from 'monitor';
import { Button, CenteredLoader, ConfirmationButton, Instruction, List, Typography } from 'ui';

import { useAssignLoadToInspection } from '../../hooks/useAssignLoadToInspection/useAssignLoadToInspection';
import { useInspectionLoadByAssignment } from '../../hooks/useInspectionLoadByAssignment/useInspectionLoadByAssignment';
import { LoadListItem } from '../LoadListItem/LoadListItem';

export const InspectionLoadAssigner = ({
  inspectionId,
  dealId,
  inspectionSlug,
}: {
  inspectionId: number;
  dealId: number;
  inspectionSlug: string;
}) => {
  const { assign, reassign, unassign } = useAssignLoadToInspection();

  const { assignedLoads, unassignedLoads, otherInspectionsLoads, isLoading } = useInspectionLoadByAssignment(
    inspectionId,
    dealId
  );

  if (isLoading) {
    return <CenteredLoader />;
  }

  if (!assignedLoads && !unassignedLoads && !otherInspectionsLoads) {
    logger.warn(`No loads found for deal ${dealId}`, {
      dealId,
      inspectionId,
    });
    return <Typography>No loads found for selected deal</Typography>;
  }

  return (
    <div>
      <Typography variant="h6">Assigned loads</Typography>
      {assignedLoads?.length ? (
        <List disablePadding>
          {assignedLoads.map((load) => (
            <LoadListItem key={load.id} load={load}>
              <Button
                variant="secondary"
                size="small"
                onClick={() => {
                  if (!load.inspection_load_id) {
                    logger.error('inspection_load_id missing during unassign', load);
                    return;
                  }
                  unassign(load.inspection_load_id);
                }}
              >
                Unassign
              </Button>
            </LoadListItem>
          ))}
        </List>
      ) : (
        <Instruction>No assigned loads found for this deal.</Instruction>
      )}
      <Typography variant="h6">Unassigned loads</Typography>
      {unassignedLoads?.length ? (
        <List disablePadding>
          {unassignedLoads.map((load) => (
            <LoadListItem key={load.id} load={load}>
              <Button
                variant="secondary"
                size="small"
                onClick={() => {
                  if (!load.inspection_load_id) {
                    assign(load.id, inspectionId);
                    return;
                  }
                  reassign(load.inspection_load_id, inspectionId);
                }}
              >
                Assign
              </Button>
            </LoadListItem>
          ))}
        </List>
      ) : (
        <Instruction>No unassigned loads found for this deal.</Instruction>
      )}
      <Typography variant="h6">Other inspection loads</Typography>
      <Typography>Loads related to this deal assigned to another inspection.</Typography>
      {otherInspectionsLoads?.length ? (
        <List disablePadding>
          {otherInspectionsLoads.map((load) => (
            <LoadListItem key={load.id} load={load}>
              <ConfirmationButton
                size="small"
                variant="secondary"
                onConfirm={() => {
                  if (!load.inspection_load_id) {
                    logger.error('inspection_load_id missing during reassign', load);
                    return;
                  }
                  reassign(load.inspection_load_id, inspectionId);
                }}
                title={`Switch load ${load.name} to inspection ${inspectionSlug}`}
                message={`This load is already assigned to Inspection ${load.inspection_slug}. Are you sure you want to reassign this load this inspection.`}
              >
                Reassign
              </ConfirmationButton>
            </LoadListItem>
          ))}
        </List>
      ) : (
        <Instruction>No other inspection loads found for this deal.</Instruction>
      )}
    </div>
  );
};
